import type { FileVersion } from "schemas/generated/fileVersion";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { useFetchPageFilesForWebContainer } from "@/features/web-container/useFetchPageFilesForWebContainer";
import Button from "@replo/design-system/components/button/Button";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSearchParams } from "react-router-dom";
import { formatDateToMonthDayHour } from "replo-utils/lib/datetime";

import {
  useChatStoreActions,
  useViewingVersionId,
} from "../chat/store/ChatStoreContext";

const INITIAL_ITEMS_TO_LOAD = 20;

const VersionCard = ({
  version,
  isViewingVersion,
  isCurrentVersion,
}: {
  version: FileVersion;
  isViewingVersion: boolean;
  isCurrentVersion: boolean;
}) => {
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const chatId = searchParams.get("chatId");
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const { setViewingVersionId } = useChatStoreActions();

  const fetchPageFiles = useFetchPageFilesForWebContainer();

  const {
    mutateAsync: revertPageToVersion,
    isPending: isRevertingPageToVersion,
  } = trpc.nextJSProject.revertPageToVersion.useMutation({
    onSuccess: () => {
      void fetchPageFiles([{ id: pageId! }]);
    },
  });

  const { mutate: addMessage } = trpc.chat.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void trpcUtils.chat.get.invalidate({
          chatId,
          projectId,
        });
      }
      void trpcUtils.fileVersion.findByFileId.invalidate({
        projectId,
        fileId: pageId!,
      });
    },
  });

  const handleRevertPageToVersion = React.useCallback(async () => {
    if (projectId && user?.id && chatId) {
      const newVersion = await revertPageToVersion({
        projectId,
        fileVersionId: version.id,
        userId: user?.id,
      });

      addMessage({
        projectId,
        chatId,
        content: [
          {
            type: "text",
            contentType: "revert" as const,
            text: newVersion.summary,
          },
        ],
        role: "user",
      });

      setViewingVersionId(newVersion.id);
    }
  }, [
    projectId,
    version.id,
    user?.id,
    revertPageToVersion,
    chatId,
    addMessage,
    setViewingVersionId,
  ]);

  const handlePreviewVersion = React.useCallback(async () => {
    if (!projectId || !pageId || !version.id) {
      return;
    }

    setPreviewLoading(true);

    await fetchPageFiles([{ id: pageId!, fileVersionId: version.id }]);
    setViewingVersionId(version.id);
    setPreviewLoading(false);
  }, [projectId, pageId, version.id, setViewingVersionId, fetchPageFiles]);

  return (
    <div
      className={twMerge(
        "flex flex-col p-3 rounded-xl",
        isViewingVersion ? "border-2 border-default" : "border border-border",
      )}
    >
      <div className="text-base font-medium mb-1">{version.summary}</div>
      <div className="text-sm text-muted">
        {formatDateToMonthDayHour(version.createdAt)}
      </div>

      <div className="flex flex-row w-full gap-2 justify-end">
        {!isCurrentVersion && (
          <Button
            onClick={() => {
              void handleRevertPageToVersion();
            }}
            isLoading={isRevertingPageToVersion}
            variant="tertiary"
          >
            Restore
          </Button>
        )}
        <Button
          disabled={isViewingVersion}
          onClick={() => {
            void handlePreviewVersion();
          }}
          isLoading={previewLoading}
          variant="secondary"
        >
          {isViewingVersion ? "Viewing" : "Preview"}
        </Button>
      </div>
    </div>
  );
};

function VersionHistory() {
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const viewingVersionId = useViewingVersionId();
  const projectId = useCurrentProjectId();

  const {
    data: fileVersionsData,
    fetchNextPage: fetchNextVersionsPage,
    hasNextPage: hasNextVersionsPage,
  } = trpc.fileVersion.findByFileId.useInfiniteQuery(
    projectId && pageId
      ? {
          projectId,
          fileId: pageId,
          limit: INITIAL_ITEMS_TO_LOAD,
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
      staleTime: 0,
    },
  );

  const fileVersions = React.useMemo(
    () => fileVersionsData?.pages?.flatMap((page) => page.fileVersions) ?? [],
    [fileVersionsData],
  );

  const currentVersion = fileVersions?.[0];

  return (
    <div className="flex flex-col w-full h-full gap-4 p-4 shadow-selectable border-0.5 border-border rounded-lg bg-white relative overflow-hidden">
      <div className="text-base font-medium">Version History</div>
      <div
        className="flex flex-col gap-2 overflow-y-auto flex-1"
        id="versions-scroll-container"
      >
        <InfiniteScroll
          dataLength={fileVersions.length}
          next={() => void fetchNextVersionsPage()}
          hasMore={hasNextVersionsPage}
          scrollableTarget="versions-scroll-container"
          loader={
            <div className="text-center p-2 flex justify-center items-center">
              <Spinner variant="primary" size={16} />
            </div>
          }
        >
          <div className="flex flex-col gap-2">
            {fileVersions?.map((version) => (
              <VersionCard
                key={version.id}
                isViewingVersion={viewingVersionId === version.id}
                isCurrentVersion={version.id === currentVersion?.id}
                version={version}
              />
            ))}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
}

export default VersionHistory;
